/**
 * Paying a vendor's bills from one bank payment -- Tally's bill-wise details.
 *
 * Opened under the debit it is about. The Book Keeper picks the vendor (the
 * screen suggests one from the narration), sees every bill of theirs with
 * something still to pay, and puts an amount against each: part of one bill,
 * all of three, whatever was actually paid. What no bill takes is an advance
 * the vendor holds against their next bill. Every rupee of the payment goes
 * somewhere, so the totals have to agree before it posts.
 *
 * A payment already posted on its own -- to an expense, which counts the bill's
 * cost twice -- can be moved onto its bills the same way. That changes what is
 * in the books, so it waits for the Finance Head, and so does an advance the
 * bank debit control has no approval for.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Note } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

export interface VendorPayment {
  id: string
  reference: string
  party_id: string
  party_name: string
  bank_transaction_id: string
  payment_date: string
  amount_paise: number
  advance_paise: number
  status: 'draft' | 'submitted' | 'posted' | 'cancelled'
  rebook: boolean
  approved: boolean
  approval_state: string | null
  notes: string | null
  allocations: {
    vendor_invoice_id: string
    reference: string
    supplier_invoice_no: string
    amount_paise: number
  }[]
  cancel_reason: string | null
}

export interface OpenBillsHint {
  party_id: string
  party_name: string
  count: number
  outstanding_paise: number
}

interface BillOption {
  id: string
  reference: string
  supplier_invoice_no: string
  invoice_date: string
  due_date: string
  payable_paise: number
  paid_paise: number
  outstanding_paise: number
  available_paise: number
  payment_status: string
  days_overdue: number
}

interface PayBillsView {
  bank_transaction_id: string
  amount_paise: number
  txn_date: string
  narration: string
  rebook: boolean
  refusal: string | null
  suggested_party_id: string | null
  party_id: string | null
  party_name: string | null
  advance_held_paise: number
  bills: BillOption[]
  payment: VendorPayment | null
}

interface Option {
  id: string
  name: string
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

const rupees = (paise: number) => (paise / 100).toFixed(2)

/** A short note for the bank line: what the payment did, or where it waits. */
export function vendorPaymentNote(p: VendorPayment): { text: string; tone: string } {
  const bills = p.allocations.length
  const what = `${bills ? `${bills} bill${bills > 1 ? 's' : ''}` : ''}${
    p.advance_paise ? `${bills ? ' + ' : ''}advance` : ''
  }`
  if (p.status === 'posted') return { text: `${p.reference}: paid ${what}`, tone: 'posted' }
  if (p.status === 'submitted')
    return p.approved
      ? { text: `${p.reference}: approved, ready to post`, tone: 'approved' }
      : { text: `${p.reference}: with the Finance Head`, tone: 'submitted' }
  return { text: `${p.reference}: ${p.status}`, tone: p.status }
}

export default function PayBills({
  txnId,
  onDone,
  onCancel,
}: {
  txnId: string
  onDone: () => void
  onCancel: () => void
}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [partyId, setPartyId] = useState<string | null>(null)
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')

  const view = useQuery({
    queryKey: ['pay-bills', txnId, partyId],
    queryFn: () =>
      api.get<PayBillsView>(
        `/banking/transactions/${txnId}/pay-bills` + (partyId ? `?party_id=${partyId}` : ''),
      ),
  })
  const vendors = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<{ vendors: Option[] }>('/spend/form-options'),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['pay-bills', txnId] })
    void queryClient.invalidateQueries({ queryKey: ['invoices'] })
    onDone()
  }
  const pay = useMutation({
    mutationFn: () =>
      api.post<VendorPayment>(`/banking/transactions/${txnId}/pay-bills`, {
        party_id: data?.party_id,
        allocations: bills
          .filter((b) => toPaise(amounts[b.id] ?? '') > 0)
          .map((b) => ({ vendor_invoice_id: b.id, amount_paise: toPaise(amounts[b.id] ?? '') })),
        advance_paise: advance,
        notes: notes.trim() || null,
      }),
    onSuccess: refresh,
  })
  const postIt = useMutation({
    mutationFn: (id: string) => api.post<VendorPayment>(`/vendor-payments/${id}/post`),
    onSuccess: refresh,
  })
  const cancel = useMutation({
    mutationFn: (id: string) =>
      api.post<VendorPayment>(
        `/vendor-payments/${id}/${data?.payment?.status === 'posted' ? 'reverse' : 'cancel'}`,
        { reason: reason.trim() },
      ),
    onSuccess: () => {
      setReason('')
      refresh()
    },
  })

  const data = view.data
  const bills = data?.bills ?? []
  const allocated = bills.reduce((t, b) => t + toPaise(amounts[b.id] ?? ''), 0)
  const amount = data?.amount_paise ?? 0
  const advance = Math.max(amount - allocated, 0)
  const over = bills.filter((b) => toPaise(amounts[b.id] ?? '') > b.available_paise)
  const tooMuch = allocated > amount

  //: Oldest first, each bill as much as it can take, until the payment runs out.
  const fillOldestFirst = () => {
    let left = amount
    const next: Record<string, string> = {}
    for (const b of bills) {
      const take = Math.min(left, b.available_paise)
      if (take <= 0) break
      next[b.id] = rupees(take)
      left -= take
    }
    setAmounts(next)
  }

  if (view.isLoading || !data) return <div className="faint">Loading the vendor’s bills…</div>

  const payment = data.payment
  if (payment) {
    const note = vendorPaymentNote(payment)
    return (
      <div className="card" style={{ padding: 12 }}>
        <ErrorNote error={postIt.error ?? cancel.error} />
        <div>
          <span className={`pill ${note.tone}`}>{note.text}</span>{' '}
          <span className="muted">
            {payment.party_name} · {formatInr(payment.amount_paise)}
            {payment.rebook && ' · moved from its own posting'}
          </span>
        </div>
        <table className="appr-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Bill</th>
              <th className="num">Paid by this</th>
            </tr>
          </thead>
          <tbody>
            {payment.allocations.map((a) => (
              <tr key={a.vendor_invoice_id}>
                <td>
                  <span className="mono">{a.supplier_invoice_no}</span>{' '}
                  <span className="ref">{a.reference}</span>
                </td>
                <td className="num">{formatInr(a.amount_paise)}</td>
              </tr>
            ))}
            {payment.advance_paise > 0 && (
              <tr>
                <td>Advance held by the vendor</td>
                <td className="num">{formatInr(payment.advance_paise)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {payment.status === 'submitted' && payment.approval_state && (
          <div className="faint">{payment.approval_state}</div>
        )}
        <div className="row" style={{ marginTop: 8, gap: 8 }}>
          {payment.status === 'submitted' && payment.approved && can('ap.payment.prepare') && (
            <button
              className="primary small"
              disabled={postIt.isPending}
              onClick={() => postIt.mutate(payment.id)}
            >
              Post the payment
            </button>
          )}
          {((payment.status === 'submitted' && can('ap.payment.prepare')) ||
            (payment.status === 'posted' && can('ap.payment.void'))) && (
            <>
              <input
                aria-label="Why it is cancelled"
                placeholder={
                  payment.status === 'posted' ? 'Why take it off the bills?' : 'Why withdraw it?'
                }
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                className="small"
                disabled={cancel.isPending || !reason.trim()}
                onClick={() => cancel.mutate(payment.id)}
              >
                {payment.status === 'posted' ? 'Cancel the payment' : 'Withdraw'}
              </button>
            </>
          )}
          <button className="small" onClick={onCancel}>
            Close
          </button>
        </div>
        {payment.status === 'posted' && !can('ap.payment.void') && (
          <div className="faint">
            Taking a payment back off its bills is the Finance Head’s, from here.
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <ErrorNote error={pay.error ?? view.error} />
      {data.refusal && <Note kind="warn">{data.refusal}</Note>}
      {data.rebook && !data.refusal && (
        <Note kind="info">
          This payment is already in the books on its own. Paying bills from it takes that entry out
          and books it as a payment of the bills instead — the Finance Head approves the move first.
        </Note>
      )}
      <div className="row">
        <Field
          label="Vendor paid"
          caption={
            data.suggested_party_id && data.suggested_party_id === data.party_id
              ? 'suggested from the narration'
              : 'whose bills this paid'
          }
        >
          <SearchSelect
            aria-label="Vendor paid"
            value={data.party_id ?? ''}
            onChange={(v) => {
              setPartyId(v || null)
              setAmounts({})
            }}
            placeholder="Choose the vendor…"
            options={(vendors.data?.vendors ?? []).map((v) => ({ value: v.id, label: v.name }))}
          />
        </Field>
        <Field label="Bank paid" caption={data.txn_date}>
          <input readOnly tabIndex={-1} className="num" value={formatInr(amount)} />
        </Field>
        <Field label="Advance already held" caption="adjust it on the bill">
          <input
            readOnly
            tabIndex={-1}
            className="num"
            value={formatInr(data.advance_held_paise)}
          />
        </Field>
      </div>

      {data.party_id && bills.length === 0 && (
        <Note kind="info">
          {data.party_name} has no bill with anything left to pay. The whole payment would be an
          advance against their next bill.
        </Note>
      )}

      {bills.length > 0 && (
        <>
          <table className="appr-table">
            <thead>
              <tr>
                <th>Bill</th>
                <th>Date / due</th>
                <th className="num">Bill amount</th>
                <th className="num">Paid so far</th>
                <th className="num">Outstanding</th>
                <th className="num" style={{ width: 150 }}>
                  Pay now (₹)
                </th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => {
                const wanted = toPaise(amounts[b.id] ?? '')
                return (
                  <tr key={b.id}>
                    <td>
                      <span className="mono">{b.supplier_invoice_no}</span>
                      <div className="cell-sub">
                        <span className="ref">{b.reference}</span>{' '}
                        <span className={`pill ${b.payment_status}`}>
                          {b.payment_status.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="muted date">
                      {b.invoice_date}
                      <div className="cell-sub">
                        due {b.due_date}
                        {b.days_overdue > 0 && ` · ${b.days_overdue} days late`}
                      </div>
                    </td>
                    <td className="num">{formatInr(b.payable_paise)}</td>
                    <td className="num">{formatInr(b.paid_paise)}</td>
                    <td className="num">
                      <strong>{formatInr(b.outstanding_paise)}</strong>
                      {b.available_paise < b.outstanding_paise && (
                        <div className="cell-sub">
                          {formatInr(b.available_paise)} free; the rest is held by a payment waiting
                          for approval
                        </div>
                      )}
                    </td>
                    <td className="num">
                      <input
                        className="num"
                        aria-label={`Pay now on ${b.supplier_invoice_no}`}
                        value={amounts[b.id] ?? ''}
                        placeholder="0.00"
                        onChange={(e) => setAmounts({ ...amounts, [b.id]: e.target.value })}
                      />
                      {wanted > 0 && wanted < b.available_paise && (
                        <div className="cell-sub">
                          leaves {formatInr(b.available_paise - wanted)}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 6 }}>
            <div className="shrink">
              <button type="button" className="small" onClick={fillOldestFirst}>
                Fill oldest first
              </button>
            </div>
          </div>
        </>
      )}

      {data.party_id && (
        <dl className="appr-facts">
          <div>
            <dt>To the bills</dt>
            <dd>{formatInr(allocated)}</dd>
          </div>
          <div>
            <dt>As an advance</dt>
            <dd>{formatInr(advance)}</dd>
          </div>
          <div>
            <dt>Bank paid</dt>
            <dd>
              <strong>{formatInr(amount)}</strong>
            </dd>
          </div>
        </dl>
      )}
      {over.length > 0 && (
        <Note kind="error">
          {over.map((b) => b.supplier_invoice_no).join(', ')}: more than is left to pay.
        </Note>
      )}
      {tooMuch && (
        <Note kind="error">
          The bills come to {formatInr(allocated)}, more than the {formatInr(amount)} the bank paid.
        </Note>
      )}
      {advance > 0 && allocated > 0 && !tooMuch && (
        <div className="faint">
          {formatInr(advance)} no bill takes will be held as an advance against {data.party_name}’s
          next bill.
        </div>
      )}

      <div className="row" style={{ marginTop: 8 }}>
        <Field label="Note" caption="optional">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <div className="row" style={{ marginTop: 8, gap: 8 }}>
        <div className="shrink">
          <button
            className="primary"
            disabled={
              pay.isPending ||
              !data.party_id ||
              !!data.refusal ||
              over.length > 0 ||
              tooMuch ||
              amount === 0
            }
            onClick={() => pay.mutate()}
          >
            {data.rebook ? 'Send the move to the Finance Head' : 'Pay these bills'}
          </button>
        </div>
        <div className="shrink">
          <button onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
